import { memo, useMemo } from 'react';
import { Grid, Divider, useEventCallback } from '@mui/material';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { FormikErrors, FormikTouched } from 'formik';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import PmiTextField from 'shell/components/shared/text-field';
import { transformExponent } from 'system/components/shared/street-number-list/transform-exponent';
import { MilanMuncipality } from 'practice/constants/muncipality';
import AddressAutoComplete from 'system/components/shared/search-address';
import StreetNumberList, { StreetNumberVM } from 'system/components/shared/street-number-list';
import CadastralData from './cadastral-data';
import AeData from './ae-data';
import { ItalianVM } from 'shell/view-models/register-office-practice-vm';

interface FormProps {
  values?: ItalianVM;
  onChange: (key: keyof ItalianVM, value: ItalianVM[keyof ItalianVM]) => void;
  onChanges: (values: ItalianVM) => void;
  errors?: FormikErrors<ItalianVM>;
  touched?: FormikTouched<ItalianVM>;
  disabled?: boolean;
  ForPracticeTemporary?: boolean;
  isEmigr?: boolean;
}

const ItalianFormComponent = ({
  values,
  onChange,
  onChanges,
  errors,
  disabled,
  ForPracticeTemporary,
  touched,
  isEmigr,
}: FormProps): React.ReactElement => {
  const onChangeMunicipalityList = useEventCallback((municipality: MunicipalityAnprVM | null) => {
    onChange('municipality', municipality);
  });

  const isEmptyDatiCatastali = useMemo(() => {
    return !Boolean(
      values?.section ||
        values?.sheet ||
        values?.particle ||
        values?.sub ||
        values?.cat ||
        values?.isHomeless
    );
  }, [values]);

  const isASSDatiCatastali = useMemo(() => {
    return Boolean(
      values?.sheet === 'ASS' || values?.particle === 'ASSENTE' || values?.sub === 'ASS'
    );
  }, [values]);

  const isEmptyDatiAe = useMemo(() => {
    return !Boolean(values?.officeContract || values?.numberContract || values?.dateContract);
  }, [values]);

  const onChangeStreetNumberList = useEventCallback((item: StreetNumberVM | null) => {
    onChanges({
      ...(values || {}),
      number: item?.id ? Number.parseInt(item.id?.toString(), 10) : undefined,
      cap: item?.cap || '',
      exponent: item?.exponent,
    });
  });

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12}>
        <PmiTitleDivider>Indirizzo</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} md={8}>
        <MunicipalityList
          label="Comune"
          value={
            !values?.municipality && ForPracticeTemporary
              ? MilanMuncipality
              : values?.municipality
              ? values?.municipality
              : null
          }
          disabled={!ForPracticeTemporary}
          onChange={(municipality) => onChangeMunicipalityList(municipality)}
          id="municipalityOfBirth"
          error={touched?.municipality && Boolean(errors?.municipality)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="C.A.P."
          aria-label="cap"
          id="cap"
          value={values?.cap || ''}
          onChange={(event) => {
            onChange('cap', event.target.value);
          }}
          fullWidth
          error={touched?.cap && Boolean(errors?.cap)}
          helperText={errors?.cap}
          disabled={!ForPracticeTemporary}
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <AddressAutoComplete
          required
          selectedValue={values?.address || null}
          handleChange={(value) => {
            onChange('address', value || null);
          }}
          error={touched?.address && Boolean(errors?.address)}
          disabled={!ForPracticeTemporary}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <StreetNumberList
          label="Numero"
          onChange={onChangeStreetNumberList}
          valueStreetNumber={
            values?.number
              ? {
                  id: values?.number,
                  value: `${values?.number?.toString()}${transformExponent(
                    values?.exponent || ''
                  )}`,
                }
              : null
          }
          showExponent
          disabled={!ForPracticeTemporary || !values?.address?.code}
          circulationArea={values?.address?.code?.toString() || ''}
          error={touched?.number && Boolean(errors?.number)}
          helperText={errors?.number}
        />
      </Grid>
      {ForPracticeTemporary ? (
        <>
          <Grid item xs={12} md={4}>
            <PmiTextField
              label="Esponente"
              aria-label="Esponente"
              id="exponent"
              value={values?.exponent || ''}
              onChange={(event) => {
                onChange('exponent', event.target.value);
              }}
              fullWidth
              error={touched?.exponent && Boolean(errors?.exponent)}
              helperText={errors?.exponent}
              disabled={!ForPracticeTemporary}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              label="Lettera"
              aria-label="Lettera"
              id="letter"
              value={values?.letter || ''}
              onChange={(event) => {
                onChange('letter', event.target.value);
              }}
              fullWidth
              error={touched?.letter && Boolean(errors?.letter)}
              helperText={errors?.letter}
              disabled={!ForPracticeTemporary}
            />
          </Grid>
        </>
      ) : undefined}
      {isEmigr ? undefined : (
        <>
          {isEmptyDatiAe ? undefined : (
            <AeData
              values={values}
              onChange={onChange}
              errors={errors}
              disabled={disabled}
              ForPracticeTemporary={ForPracticeTemporary}
              touched={touched}
            />
          )}
          {isASSDatiCatastali || !isEmptyDatiCatastali ? (
            <CadastralData
              values={values}
              onChange={onChange}
              errors={errors}
              disabled={disabled}
              ForPracticeTemporary={ForPracticeTemporary}
              touched={touched}
            />
          ) : undefined}
        </>
      )}
    </Grid>
  );
};

ItalianFormComponent.displayName = 'Accordion New Residence Form - Italian Form';

const ItalianForm = memo(ItalianFormComponent);

export default ItalianForm;
